import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { MONEY_LABELS, MONEY_PROVENANCE, MONEY_TIPS } from '../../../../constants/deliveryMoney';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { MaskedField } from '../../../../lib/access/MaskedField';
import { programRef } from '../../../../lib/access/types';
import { formatUtcYmd, parseUtcDate } from '../../_shared/api/nullable';
import { ConfirmDeleteDialog } from '../../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../../_shared/components/EntityFormDialog';
import { EntityTable } from '../../_shared/components/EntityTable';
import { FUNDED_NOT_EXERCISED } from '../../_shared/components/review/reviewItemCopy';
import { ScoreDetailDrawer } from '../../_shared/components/ScoreDetailDrawer';
import { formatMoney } from '../../_shared/lib/recordLabels';
import { popHooks } from '../operationalHooks';
import { PeriodModificationHistory } from './PeriodModificationHistory';
// Values MUST match the pangaea chk_pop_period_type constraint
// (BASE/OPTION/EXTENSION/BRIDGE) — sending lowercase / option_period 500s
// every UpsertPeriodOfPerformance write.
const periodTypeOptions = [
    {
        value: 'BASE',
        label: 'Base'
    },
    {
        value: 'OPTION',
        label: 'Option Period'
    },
    {
        value: 'EXTENSION',
        label: 'Extension'
    },
    {
        value: 'BRIDGE',
        label: 'Bridge'
    }
];
// An option period funded with no exercise on file reads "Funded, not
// exercised" (D116): funding is not an exercise. It tells funded money is
// there, so a viewer who can't see the funded amount reads plain "No". The
// base period is exercised by definition, so it never reads that way.
function exercisedLabel(p, resource) {
    if (p.exercised) return 'Yes';
    if (p.fundedAmount <= 0 || !(p.periodType ?? '').toUpperCase().includes('OPTION')) return 'No';
    return /*#__PURE__*/ _jsx(MaskedField, {
        fieldName: "funded_amount",
        resource: resource,
        placeholder: "No",
        children: FUNDED_NOT_EXERCISED
    });
}
const popColumns = (resource)=>[
        {
            key: 'periodName',
            header: 'Period'
        },
        {
            key: 'periodType',
            header: 'Type'
        },
        {
            key: 'exercised',
            header: 'Exercised',
            render: (row)=>exercisedLabel(row, resource)
        },
        {
            key: 'startDate',
            header: 'Start',
            render: (row)=>row.startDate ? formatUtcYmd(row.startDate) ?? '—' : '—'
        },
        {
            key: 'endDate',
            header: 'End',
            render: (row)=>row.endDate ? formatUtcYmd(row.endDate) ?? '—' : '—'
        },
        {
            key: 'fundedAmount',
            header: MONEY_LABELS.funded,
            headerInfo: MONEY_TIPS.funded,
            render: (row)=>formatMoney(row.fundedAmount),
            maskAs: 'funded_amount'
        },
        {
            key: 'totalAmount',
            header: MONEY_LABELS.totalValue,
            headerInfo: MONEY_TIPS.totalValue,
            render: (row)=>formatMoney(row.totalAmount),
            maskAs: 'total_amount'
        }
    ];
const fields = [
    {
        kind: 'text',
        name: 'periodName',
        label: 'Period name',
        required: true,
        maxLength: 200
    },
    {
        kind: 'select',
        name: 'periodType',
        label: 'Type',
        required: true,
        options: periodTypeOptions
    },
    {
        kind: 'checkbox',
        name: 'exercised',
        label: 'Exercised'
    },
    // Not required: manually-added periods may legitimately lack dates.
    // No money fields: committed modifications are the only writer of
    // funded_amount / total_amount (D7) — the dialog's description says so.
    {
        kind: 'date',
        name: 'startDate',
        label: 'Start date'
    },
    {
        kind: 'date',
        name: 'endDate',
        label: 'End date'
    }
];
// Form defaults for a given record (null = create). Shared by the create modal
// and the drawer's inline edit so both surfaces start from the same shape.
const popDefaults = (p)=>({
        id: p?.id ?? '',
        periodName: p?.periodName ?? '',
        periodType: p?.periodType ?? '',
        optionNumber: p?.optionNumber ?? 0,
        exercised: p?.exercised ?? false,
        startDate: p?.startDate ? formatUtcYmd(p.startDate) ?? '' : '',
        endDate: p?.endDate ? formatUtcYmd(p.endDate) ?? '' : ''
    });
const ymd = (t)=>t ? formatUtcYmd(t) ?? '—' : '—';
// Read sections plus, per row, the inline-edit control. Every row the create
// form covers carries an `editField` (bound to the drawer's form) plus the
// option number, which the drawer surfaces in the Period section. Funded /
// total amounts wrap their READ value in MaskedField per-token (the two use
// different mask tokens, so the drawer's single-token mask can't cover both) —
// only a writer (canEdit) enters edit, and writers see full values, so the
// currency control is not a leak.
function popDetailSections(p, resource) {
    const currency = (n)=>formatMoney(n);
    return [
        {
            heading: 'Period',
            layout: 'columns',
            rows: [
                {
                    label: 'Period name',
                    value: p.periodName || '—',
                    editField: {
                        kind: 'text',
                        name: 'periodName',
                        label: 'Period name',
                        required: true,
                        maxLength: 200
                    }
                },
                {
                    label: 'Type',
                    value: periodTypeOptions.find((o)=>o.value === p.periodType)?.label ?? (p.periodType || '—'),
                    editField: {
                        kind: 'select',
                        name: 'periodType',
                        label: 'Type',
                        required: true,
                        options: periodTypeOptions
                    }
                }
            ]
        },
        {
            heading: 'Dates',
            layout: 'columns',
            rows: [
                {
                    label: 'Start',
                    value: ymd(p.startDate),
                    editField: {
                        kind: 'date',
                        name: 'startDate',
                        label: 'Start date'
                    }
                },
                {
                    label: 'End',
                    value: ymd(p.endDate),
                    editField: {
                        kind: 'date',
                        name: 'endDate',
                        label: 'End date'
                    }
                }
            ]
        },
        {
            heading: 'Value',
            layout: 'columns',
            // The money rows carry no editField ON PURPOSE (D7): committed
            // modifications are the only writer of funded_amount / total_amount, so
            // in edit mode these stay read-only figures while dates/name edit. The
            // MONEY_TIPS copy on each label is the pointer to where money comes from.
            rows: [
                {
                    label: MONEY_LABELS.funded,
                    labelInfo: MONEY_TIPS.funded,
                    value: /*#__PURE__*/ _jsx(MaskedField, {
                        fieldName: "funded_amount",
                        resource: resource,
                        children: currency(p.fundedAmount)
                    })
                },
                {
                    label: MONEY_LABELS.totalValue,
                    labelInfo: MONEY_TIPS.totalValue,
                    value: /*#__PURE__*/ _jsx(MaskedField, {
                        fieldName: "total_amount",
                        resource: resource,
                        children: currency(p.totalAmount)
                    })
                },
                {
                    label: 'Exercised',
                    value: exercisedLabel(p, resource),
                    editField: {
                        kind: 'checkbox',
                        name: 'exercised',
                        label: 'Exercised'
                    }
                }
            ]
        }
    ];
}
export function PeriodsOfPerformanceTab({ programId }) {
    // Server-computed edit capability (GetProgramResponse.can_edit — the
    // "update" verb on the program's scope node), surfaced by ProgramLayout.
    // NOTE the known platform-wide asymmetry (same as SAD-73's Capabilities
    // fix): the period write gate additionally requires area:execution, so an
    // update-verb bundle without that area (e.g. quality_audit) sees enabled
    // affordances and gets the server's PermissionDenied on submit — the safe
    // direction, but real; per-area capability flags are the follow-up.
    const { canEdit } = useProgramContext();
    const { data, isLoading } = popHooks.useList({
        programId
    });
    const upsert = popHooks.useUpsert();
    const remove = popHooks.useDelete();
    const items = data?.items ?? [];
    const resource = programRef(programId);
    const [formOpen, setFormOpen] = useState(false);
    const [deleting, setDeleting] = useState(null);
    // Store only the selected id and DERIVE the record from `items` — so an open
    // drawer always reflects the latest list (e.g. after an inline-edit Save)
    // without an effect that re-syncs state (which infinite-loops when the list
    // returns fresh-but-equal references each render).
    const [detailId, setDetailId] = useState(null);
    const detail = detailId ? items.find((p)=>p.id === detailId) ?? null : null;
    // Whether the detail drawer opens in read or straight into edit (row Edit).
    const [detailMode, setDetailMode] = useState('read');
    // Shared by the create modal and drawer edit-in-place; the target id is passed
    // in (empty = create). The upsert is full-replace, so every field is sent.
    const submitPoP = async (values, id)=>{
        // YMD strings compare lexicographically; the throw renders inline.
        if (values.startDate && values.endDate && values.endDate < values.startDate) {
            throw new Error('End date must be on or after start date');
        }
        const src = id ? items.find((p)=>p.id === id) ?? null : null;
        await upsert.mutateAsync({
            id,
            programId,
            periodName: values.periodName,
            periodType: values.periodType,
            // Option # is seeded from the record and surfaced in the drawer's Period
            // section, so an untouched edit re-sends whatever a sync import assigned
            // (baltigo numbers imported option periods 1, 2, …) instead of NULLing it
            // to 0. New manual periods default to 0.
            optionNumber: Math.trunc(Number(values.optionNumber) || 0),
            exercised: Boolean(values.exercised),
            // No money is sent at all (D7: mods are the only writer). The server is
            // the rail, twice over: ryugu ignores request money (stored figures ride
            // through updates, creates start unfunded) and pangaea's generic Update
            // omits the money columns — so the full-replace shape can't zero them
            // (caveat C4) no matter what a client sends.
            // Always sent: the handler is full-replace, so omitting dates on an
            // imported period would NULL them (the wipe this branch fixes).
            startDate: values.startDate ? parseUtcDate(values.startDate) : undefined,
            endDate: values.endDate ? parseUtcDate(values.endDate) : undefined,
            sortOrder: src ? src.sortOrder : Math.max(0, ...items.map((p)=>p.sortOrder).filter(Number.isFinite)) + 1
        });
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    // The drawer's inline-edit config, bound to the viewed record.
    const detailEdit = detail ? {
        defaultValues: popDefaults(detail),
        onSubmit: (v)=>submitPoP(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes'
    } : undefined;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>setFormOpen(true),
                    disabled: !canEdit,
                    "data-testid": Testids.deliveryOperational.popCreate,
                    children: "Add Period"
                })
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: popColumns(resource),
                testid: Testids.deliveryOperational.popTable,
                rowTestid: Testids.deliveryOperational.popRow,
                rowKey: (p)=>p.id,
                isLoading: isLoading,
                hideRowActions: !canEdit,
                resource: resource,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                // Edit-in-place: open the drawer straight into edit mode.
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryOperational.popEdit,
                                children: "Edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryOperational.popDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Add Period of Performance",
                description: MONEY_PROVENANCE,
                fields: fields,
                defaultValues: popDefaults(null),
                onSubmit: async (v)=>{
                    await submitPoP(v, '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Period",
                description: deleting ? `Delete period "${deleting.periodName}"? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? detail.periodName || 'Period of Performance' : '',
                sections: detail ? popDetailSections(detail, resource) : [],
                testid: Testids.deliveryOperational.popDetailDrawer,
                edit: detailEdit,
                canEdit: canEdit,
                initialMode: detailMode,
                children: detail && /*#__PURE__*/ _jsx(PeriodModificationHistory, {
                    period: detail,
                    programId: programId,
                    resource: resource
                })
            })
        ]
    });
}
